import React, { useMemo } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { branding } from '../config/branding';
import { roleLabel, loginPortalForRole } from '../config/rbac';
import { getNavSectionsForUser } from '../lib/navFilter';
import { IconChevronDown, IconLogOut } from './Icons';
import { BrandLogo } from './BrandLogo';

function adminPhotoUrl(admin) {
  if (!admin || typeof admin !== 'object') return '';
  return admin.avatarUrl || admin.photoUrl || admin.profileImageUrl || admin.imageUrl || '';
}

function initialsFromAdmin(admin) {
  const name = (admin?.name || admin?.email || '?').trim();
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase() || '?';
}

export function AppLayout() {
  const { admin, role, logout, hasPermission } = useAuth();
  const navigate = useNavigate();

  const visibleNav = useMemo(
    () => getNavSectionsForUser({ role, hasPermission }),
    [role, hasPermission],
  );

  async function handleLogout() {
    await logout();
    const portal = loginPortalForRole(role);
    navigate(portal === 'brand' ? '/brand/login' : '/login', { replace: true });
  }

  const displayName = admin?.name || admin?.email || 'Admin';
  const photo = adminPhotoUrl(admin);
  const initials = useMemo(() => initialsFromAdmin(admin), [admin]);
  const roleBadge = roleLabel(role);
  const topbarContext = admin?.brandName ? admin.brandName : branding.productSubtitle;

  return (
    <div className="container-fluid">
      <div className="row flex-nowrap min-vh-100">
        <aside className="app-sidebar flex-shrink-0 d-flex flex-column" aria-label="Main navigation">
          <div className="app-sidebar-brand">
            <div className="app-sidebar-brand-inner">
              <BrandLogo className="app-sidebar-brand-mark" alt="" />
              <div className="min-w-0">
                <div className="brand-title app-sidebar-brand-title lh-sm">{branding.productName}</div>
                <div className="app-sidebar-brand-sub font-sans text-truncate" title={topbarContext}>
                  {topbarContext}
                </div>
              </div>
            </div>
          </div>
          <nav className="app-sidebar-nav flex-grow-1 overflow-auto">
            {visibleNav.map((section) => (
              <div key={section.label} className="app-sidebar-section">
                <div className="app-sidebar-section-label">{section.label}</div>
                <ul className="app-sidebar-list list-unstyled mb-0">
                  {section.items.map((item) => (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.end}
                        className={({ isActive }) => `app-sidebar-link${isActive ? ' is-active' : ''}`}
                      >
                        {({ isActive }) => (
                          <>
                            <span className={`app-sidebar-link-icon${isActive ? ' is-active' : ''}`} aria-hidden>
                              <item.Icon size={18} strokeWidth={isActive ? 2.25 : 2} />
                            </span>
                            <span className="app-sidebar-link-label">{item.label}</span>
                            {isActive ? <span className="app-sidebar-link-glow" aria-hidden /> : null}
                          </>
                        )}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
          <footer className="app-sidebar-footer font-sans">
            <span className="app-sidebar-footer-dot" aria-hidden />
            {branding.sidebarFooter}
          </footer>
        </aside>
        <main className="col p-0 min-vh-100 d-flex flex-column bg-light-subtle min-w-0">
          <header className="app-topbar border-bottom bg-white px-3 px-lg-4 py-2 d-flex align-items-center justify-content-between flex-shrink-0 shadow-sm gap-3">
            <div className="min-w-0">
              <p className="text-secondary small mb-0 d-none d-md-block text-truncate">{branding.productName}</p>
              {admin?.brandName ? (
                <p className="small text-dark mb-0 fw-semibold text-truncate d-none d-md-block">{admin.brandName}</p>
              ) : null}
            </div>
            <span className="badge rounded-pill text-bg-light border text-secondary d-none d-sm-inline">
              {roleBadge}
            </span>
            <div className="dropdown ms-auto">
              <button
                type="button"
                className="btn btn-light border-0 rounded-3 d-flex align-items-center gap-2 py-2 ps-2 pe-3 shadow-none app-user-trigger"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                id="adminUserMenu"
              >
                {photo ? (
                  <img src={photo} alt="" className="app-user-avatar rounded-circle object-fit-cover" width={40} height={40} />
                ) : (
                  <span className="app-user-avatar app-user-avatar-initials rounded-circle d-inline-flex align-items-center justify-content-center fw-semibold">
                    {initials}
                  </span>
                )}
                <span className="text-start d-none d-sm-block">
                  <span className="d-block fw-semibold text-dark text-truncate" style={{ maxWidth: '11rem' }} title={displayName}>
                    {displayName}
                  </span>
                  <span className="d-block small text-secondary text-truncate" style={{ maxWidth: '11rem' }} title={admin?.email}>
                    {admin?.email}
                  </span>
                </span>
                <IconChevronDown size={16} className="text-secondary flex-shrink-0" />
              </button>
              <ul className="dropdown-menu dropdown-menu-end shadow border-0 py-2 mt-2" aria-labelledby="adminUserMenu">
                <li>
                  <button type="button" className="dropdown-item d-flex align-items-center gap-2 text-danger" onClick={handleLogout}>
                    <IconLogOut size={18} />
                    Sign out
                  </button>
                </li>
              </ul>
            </div>
          </header>
          <div className="app-main-content flex-grow-1 p-4 min-w-0">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
